import { useEffect, useRef, useState } from 'react';
import { ArrowDown, Download } from 'react-bootstrap-icons';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import PromptInput from '../components/PromptInput';
import ChatbotResponse from '../components/chat/ChatbotResponse';
import TypingIndicator from '../components/loaders/TypingIndicator';
import SuggestionChips from '../components/suggestion/SuggestionChips';
import { useChatContext } from '../context/ChatProvider';
import { ChatModalType, ChatMode } from '../lib/types';
import { formatChatConversationStartedAt } from '../../../helpers/dateHelper';
import './chatMain.scss';

const ChatMainScreen = () => {
  const [prompt, setPrompt] = useState('');
  const hasRequestedConversationStartRef = useRef(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScrollRef = useRef(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const {
    onboardingInput,
    messages,
    isTyping,
    isStartingConversation,
    chatMode,
    conversationId,
    conversationStartedAt,
    preappointmentQuestionnaireSummary,
    suggestedResponses,
    setChatMode,
    setModalType,
    setSuggestedResponses,
    startConversation,
    sendMessage,
  } = useChatContext();

  const latestAssistantMessageId = [...messages]
    .reverse()
    .find((message) => message.role === 'assistant')?.id;

  const updateScrollState = (syncAutoScroll = false) => {
    const element = contentRef.current;
    if (!element) {
      return;
    }

    const bottomThreshold = 24;
    const showButtonThreshold = element.clientHeight / 2;
    const distanceFromBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight;
    const canScroll =
      element.scrollHeight > element.clientHeight + bottomThreshold;
    const nextIsAtBottom = !canScroll || distanceFromBottom <= bottomThreshold;

    if (syncAutoScroll) {
      shouldAutoScrollRef.current = nextIsAtBottom;
    }
    setShowScrollToBottom(
      canScroll && distanceFromBottom > showButtonThreshold,
    );
  };

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    const element = contentRef.current;
    if (!element) {
      return;
    }

    element.scrollTo({
      top: element.scrollHeight,
      behavior,
    });
  };

  useEffect(() => {
    if (!shouldAutoScrollRef.current) {
      requestAnimationFrame(() => {
        updateScrollState();
      });
      return;
    }

    scrollToBottom('auto');

    requestAnimationFrame(() => {
      scrollToBottom('auto');
      requestAnimationFrame(() => {
        scrollToBottom('auto');
        updateScrollState();
      });
    });
  }, [messages, isTyping]);

  useEffect(() => {
    updateScrollState(true);
  }, []);

  useEffect(() => {
    const element = messagesContainerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (!shouldAutoScrollRef.current) {
        return;
      }

      scrollToBottom('auto');
      requestAnimationFrame(() => {
        scrollToBottom('auto');
        updateScrollState();
      });
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (conversationId || hasRequestedConversationStartRef.current) {
      return;
    }

    const hasPendingConversation =
      Boolean(onboardingInput.trim()) ||
      Boolean(preappointmentQuestionnaireSummary?.trim());

    if (!hasPendingConversation) {
      return;
    }

    hasRequestedConversationStartRef.current = true;
    void startConversation();
  }, [
    conversationId,
    onboardingInput,
    preappointmentQuestionnaireSummary,
    startConversation,
  ]);

  const onSubmit = async () => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) {
      return;
    }

    setPrompt('');
    setChatMode(ChatMode.NONE);
    setSuggestedResponses((currentSuggestions) =>
      currentSuggestions.map((suggestion) => ({
        ...suggestion,
        selected: undefined,
      })),
    );

    try {
      await sendMessage(trimmedPrompt);
    } catch (error) {
      setPrompt(trimmedPrompt);
      throw error;
    }
  };

  const onSelectSuggestion = (selectedSuggestionId: string, text: string) => {
    setPrompt(text);
    setChatMode(ChatMode.TIPS);
    setSuggestedResponses((currentSuggestions) =>
      currentSuggestions.map((suggestion) => ({
        ...suggestion,
        selected:
          suggestion.id === selectedSuggestionId ? suggestion.text : undefined,
      })),
    );
  };

  return (
    <div className="chat-main">
      <button
        type="button"
        className="chat-main__end-button"
        onClick={() => setModalType(ChatModalType.END)}
      >
        <span className="chat-main__end-button-text">
          End chat + export content
        </span>
        <Download size={16} className="chat-main__end-button-icon" />
      </button>

      <div
        className="chat-main__content"
        ref={contentRef}
        onScroll={() => updateScrollState(true)}
      >
        {conversationStartedAt ? (
          <div className="chat-main__divider">
            <span className="chat-main__divider-text">
              {formatChatConversationStartedAt(conversationStartedAt)}
            </span>
          </div>
        ) : null}

        <div className="chat-main__messages" ref={messagesContainerRef}>
          {messages.map((message) =>
            message.role === 'assistant' ? (
              <ChatbotResponse
                key={message.id}
                message={message}
                showFeedbackActions={message.id === latestAssistantMessageId}
              />
            ) : (
              <div key={message.id} className="chat-main__human-row">
                <div className="chat-main__human-content">
                  <div className="chat-main__human-message">
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                      {message.text}
                    </ReactMarkdown>
                    <div className="chat-main__message-time chat-main__message-time--human">
                      {message.createdAt}
                    </div>
                  </div>
                </div>
              </div>
            ),
          )}
          {isTyping ? (
            <div className="chat-main__typing-row">
              <TypingIndicator />
            </div>
          ) : null}
        </div>
      </div>

      {showScrollToBottom ? (
        <button
          type="button"
          className="chat-main__scroll-to-bottom"
          aria-label="Scroll to latest message"
          onClick={() => {
            shouldAutoScrollRef.current = true;
            scrollToBottom('smooth');
            requestAnimationFrame(() => {
              scrollToBottom('auto');
              updateScrollState();
            });
          }}
        >
          <ArrowDown size={24} className="chat-main__scroll-to-bottom-icon" />
        </button>
      ) : null}

      <div className="chat-main__footer">
        {chatMode === ChatMode.TIPS ? (
          <SuggestionChips
            suggestions={suggestedResponses}
            onSelect={onSelectSuggestion}
          />
        ) : null}

        <PromptInput
          value={prompt}
          placeholder="Ask something..."
          onChange={setPrompt}
          onSubmit={onSubmit}
          disabled={isStartingConversation || isTyping}
        />
      </div>
    </div>
  );
};

export default ChatMainScreen;
